import { keepPreviousData, useQuery } from '@tanstack/react-query'
import * as z from 'zod'

import { getListPartnerCouponsQueryKey, listPartnerCoupons } from '@/api/gen'
import {
  type CouponListFilters,
  couponListParams,
  DEFAULT_COUPON_LIST_FILTERS,
} from '@/domain/coupon-filters'
import { couponStatusCountsSchema } from '@/domain/coupon-status'
import { affiliateAdminCouponSchema } from '@/domain/coupons'

/**
 * One page of the partner's links, as the endpoint answers it.
 *
 * `total` counts every code matching the request's filters, `status_counts`
 * spans all five rungs regardless of the `status` filter — so the two are equal
 * only while no status is selected, and the pills stay put as the admin clicks
 * between them.
 */
export const partnerCouponPageSchema = z.object({
  coupons: z.array(affiliateAdminCouponSchema),
  total: z.number(),
  status_counts: couponStatusCountsSchema,
})

export type PartnerCouponPage = z.infer<typeof partnerCouponPageSchema>

/**
 * ONE request per view. Every narrowing, the ordering and the page ride the
 * query string (see `couponListParams`), so a partner past any size is fully
 * reachable by paging and the counts describe the whole matching set rather than
 * the rows this load happened to fetch.
 *
 * The response is parsed rather than trusted: `status_counts` drives the pills
 * and `total` drives the pager, so a BE that stopped sending them must fail the
 * load loudly instead of rendering as an empty, mis-paged list.
 */
export const fetchPartnerCouponPage = async (
  partnerId: string,
  filters: CouponListFilters = DEFAULT_COUPON_LIST_FILTERS,
  signal?: AbortSignal,
): Promise<PartnerCouponPage> =>
  partnerCouponPageSchema.parse(
    await listPartnerCoupons(partnerId, couponListParams(filters), signal),
  )

/**
 * Keyed through the GENERATED helper, which appends the params object to the
 * endpoint's key. Two properties matter and are easy to break:
 *
 * - every create/edit/batch/bulk invalidation targets
 *   `getListPartnerCouponsQueryKey(partnerId)`, which TanStack matches as a
 *   PREFIX — so it still reaches every paged/filtered bucket beneath it;
 * - `couponListParams` omits keys at their default, so the resting view keeps
 *   one cache entry rather than minting a new one per equivalent request.
 */
const partnerCouponPageKey = (partnerId: string, filters: CouponListFilters) =>
  getListPartnerCouponsQueryKey(partnerId, couponListParams(filters))

/**
 * The page the admin Links table is looking at — the ONE hook that reads rows,
 * so the table, the pager and the pills can never disagree about what the server
 * matched.
 *
 * `keepPreviousData` holds the outgoing page on screen while the next one loads.
 * Without it the row set empties between pages, and the filter bar — which only
 * renders when there is something to filter — would unmount the very control the
 * admin just used, with no way back.
 */
export const usePartnerCouponPage = (partnerId: string, filters: CouponListFilters) =>
  useQuery({
    queryKey: partnerCouponPageKey(partnerId, filters),
    queryFn: ({ signal }) => fetchPartnerCouponPage(partnerId, filters, signal),
    placeholderData: keepPreviousData,
  })

/**
 * How many links the partner has in total, for the tab badge.
 *
 * Its own unfiltered read, on a fixed key: the badge counts the partner's whole
 * set, so it must not move when the table scopes itself to a tag, a batch or a
 * search — and `status_counts` would not do either, since every non-status
 * filter still narrows it. `page_size: 1` keeps the read to the envelope; the
 * one row it carries is discarded.
 */
const COUPON_COUNT_FILTERS: CouponListFilters = { ...DEFAULT_COUPON_LIST_FILTERS, pageSize: 1 }

export const usePartnerCouponCount = (partnerId: string) =>
  useQuery({
    queryKey: partnerCouponPageKey(partnerId, COUPON_COUNT_FILTERS),
    queryFn: ({ signal }) => fetchPartnerCouponPage(partnerId, COUPON_COUNT_FILTERS, signal),
    select: (page: PartnerCouponPage) => page.total,
  })
